import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { personal, contactLinks } from '../data/portfolioData';
import './Contact.css';

// ── EmailJS config — set these in .env.local ────────────────────────────────
// REACT_APP_EMAILJS_SERVICE_ID  = service_xxxxx
// REACT_APP_EMAILJS_TEMPLATE_ID = template_xxxxx
// REACT_APP_EMAILJS_PUBLIC_KEY  = your_public_key
const SVC  = process.env.REACT_APP_EMAILJS_SERVICE_ID  || 'YOUR_SERVICE_ID';
const TPL  = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
const KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY  || 'YOUR_PUBLIC_KEY';

const INIT = { name:'', email:'', subject:'', message:'' };

export default function Contact() {
  const [form,   setForm]   = useState(INIT);
  const [status, setStatus] = useState('idle');   // idle | sending | success | error
  const [err,    setErr]    = useState('');

  const onChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setStatus('sending'); setErr('');

    // emailjs.send() takes a plain object — works without a <form> ref
    const templateParams = {
      from_name:    form.name,
      from_email:   form.email,
      subject:      form.subject || '(No subject)',
      message:      form.message,
      reply_to:     form.email,
    };

    try {
      await emailjs.send(SVC, TPL, templateParams, KEY);
      setStatus('success');
      setForm(INIT);
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
      setErr(`Failed to send (${err?.text || err?.message || 'unknown error'}). Please email me directly.`);
    }
  };

  return (
    <div id="contact" className="contact-section">
      <div className="contact-inner">
        {/* ── Left info ────────────────────────────────────────────────────── */}
        <div className="contact-info">
          <div className="section-label">Contact</div>
          <h2 className="section-title">
            Let's work
            <br />
            together
          </h2>
          <p className="contact-blurb">
            If you have a project, need consultancy, want to discuss a blockchain
            DApp, or explore a collaboration — I'd love to hear from you.
          </p>

          <div className="contact-links">
            {contactLinks.map((link) => (
              <ContactLink key={link.id} link={link} />
            ))}
          </div>
        </div>

        {/* ── Right form ───────────────────────────────────────────────────── */}
        <div className="contact-form-wrap">
          {status === "success" ? (
            <div className="form-success">
              <div className="success-emoji">✅</div>
              <h3>Message sent!</h3>
              <p>
                Thanks for reaching out. I'll get back to you within 24 hours.
              </p>
              <button className="btn-send" onClick={() => setStatus("idle")}>
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="name">
                    Name *
                  </label>
                  <input
                    id="name"
                    name="name"
                    className="form-input"
                    type="text"
                    placeholder="John Doe"
                    value={form.name}
                    onChange={onChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="email">
                    Email *
                  </label>
                  <input
                    id="email"
                    name="email"
                    className="form-input"
                    type="email"
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={onChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="subject">
                  Subject
                </label>
                <input
                  id="subject"
                  name="subject"
                  className="form-input"
                  type="text"
                  placeholder="Project inquiry / Job opportunity"
                  value={form.subject}
                  onChange={onChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="message">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  className="form-input"
                  placeholder="Tell me about your project, timeline, and what you need..."
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  required
                />
              </div>

              {status === "error" && (
                <div className="form-error">
                  {err} —{" "}
                  <a href={`mailto:${personal.email}`} className="form-link">
                    {personal.email}
                  </a>
                </div>
              )}

              <button
                type="submit"
                className="btn-send"
                disabled={status === "sending"}
              >
                {status === "sending" ? (
                  <>
                    <span className="spinner" />
                    Sending…
                  </>
                ) : (
                  <>Send Message ✉️</>
                )}
              </button>

              <p className="form-note">
                Or reach me directly at{" "}
                <a href={`mailto:${personal.email}`} className="form-link">
                  {personal.email}
                </a>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactLink({ link }) {
  const body = (
    <>
      <div className="clink-icon" style={{ background: link.iconBg }}>{link.icon}</div>
      <div className="clink-body">
        <div className="clink-label">{link.label}</div>
        {/* <div className="clink-value">{link.value}</div> */}
      </div>
      {link.href && <span className="clink-arrow">→</span>}
    </>
  );
  if (link.href) {
    return (
      <a href={link.href} className="clink"
        target={link.href.startsWith('http') ? '_blank' : undefined}
        rel="noopener noreferrer">{body}</a>
    );
  }
  return <div className="clink clink--static">{body}</div>;
}
